/**
 * Project API — project reads + CRUD.
 * Uses the shared `api` instance (base '/api/v1', envelope-normalised errors).
 * ADMIN / PROJECT_MANAGER create / edit projects and archive them (status →
 * CANCELLED) from the account workspace. There is no hard delete (children FK
 * the project). Mutations invalidate the project + account-dashboard keys.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { ProjectDetail } from '@/types/workItem';
import type { ProjectStatus } from '@/types/domain';
import type { SprintMode } from '@/types/sprint';
import type { BacklogFieldSpec } from '@/types/backlogField';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

/** Create body — accountId + name + code + type required. recordPrefix/
 *  backlogFields are the M3 schema-builder wizard's output: omitting
 *  backlogFields (or sending an empty array) yields a trio-only schema and
 *  the "CR" default prefix — see CreateProjectCommandService.persistBacklogSchema. */
export interface ProjectCreatePayload {
  accountId: string;
  name: string;
  code: string;
  type: string;
  description?: string | null;
  ownerMemberId?: string | null;
  startDate?: string | null;
  targetEndDate?: string | null;
  recordPrefix?: string | null;
  backlogFields?: BacklogFieldSpec[];
}

/** Update body — name + code + type required (no accountId; account is fixed).
 *  recordPrefix is applied only when present, same convention as sprintMode. */
export interface ProjectUpdatePayload {
  name: string;
  code: string;
  type: string;
  description?: string | null;
  ownerMemberId?: string | null;
  startDate?: string | null;
  targetEndDate?: string | null;
  forecastEndDate?: string | null;
  /** Applied only when present — omit to leave the project's current mode/cadence untouched. */
  sprintMode?: SprintMode;
  sprintCadenceWeeks?: number;
  recordPrefix?: string | null;
}

async function fetchProject(id: string): Promise<ProjectDetail> {
  const res = await api.get<ApiEnvelope<ProjectDetail>>(`/project/${id}`);
  return res.data.data;
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id),
    staleTime: 60_000,
    enabled: !!id,
  });
}

async function fetchProjects(): Promise<ProjectDetail[]> {
  const res = await api.get<ApiEnvelope<ProjectDetail[]>>('/project');
  return res.data.data;
}

/** Lightweight project list — used by the sidebar nav. */
export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations — every write refreshes the project keys + the account dashboard
// table (['dashboard-account', accountId]) that hosts the create/edit surface.
// ---------------------------------------------------------------------------

function invalidateProject(qc: ReturnType<typeof useQueryClient>, accountId: string, id?: string) {
  void qc.invalidateQueries({ queryKey: ['projects'] });
  void qc.invalidateQueries({ queryKey: ['dashboard-account', accountId] });
  if (id) void qc.invalidateQueries({ queryKey: ['project', id] });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProjectCreatePayload) =>
      api.post<ApiEnvelope<ProjectDetail>>('/project', payload).then((res) => res.data.data),
    onSuccess: (item) => invalidateProject(qc, item.accountId, item.id),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accountId, payload }: { id: string; accountId: string; payload: ProjectUpdatePayload }) =>
      api.put<ApiEnvelope<ProjectDetail>>(`/project/${id}`, payload).then((res) => ({ item: res.data.data, accountId })),
    onSuccess: ({ item, accountId }) => invalidateProject(qc, accountId, item.id),
  });
}

export function useSetProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accountId, status }: { id: string; accountId: string; status: ProjectStatus }) =>
      api.patch<ApiEnvelope<void>>(`/project/${id}/status`, { status }).then(() => ({ id, accountId })),
    onSuccess: ({ id, accountId }) => invalidateProject(qc, accountId, id),
  });
}
